import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, onSnapshot } from "firebase/firestore";
import Lottie from "lottie-react";
import loadingAnimation2 from "../lottie/loading-animation-2.json"; // Adjust path if needed

function QuizSection({ auth, db }) {
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [downloadedTopics, setDownloadedTopics] = useState([]);
  const [quizMeta, setQuizMeta] = useState({}); // shareCode -> { quizId, type, title }
  const [quizCache, setQuizCache] = useState({}); // Cache for downloaded quizzes

  // Fetch user's downloadedTopics and selectedTopics
  useEffect(() => {
    const fetchUserTopics = async () => {
      if (!auth.currentUser) return;
      try {
        const userUID = auth.currentUser.uid;
        const userDocRef = doc(db, "users", userUID);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setDownloadedTopics(userData.downloadedTopics || []);
          setSelectedTopics(userData.selectedTopics || []);
          console.log("User downloadedTopics:", userData.downloadedTopics);
          console.log("User selectedTopics:", userData.selectedTopics);
        }
      } catch (error) {
        console.error("Failed to fetch user topics:", error.message);
      }
    };
    fetchUserTopics();
  }, [auth, db]);

  // Fetch quiz metadata for all downloadedTopics using shareCodeLinks
  useEffect(() => {
    const fetchQuizMeta = async () => {
      if (!auth.currentUser) return;
      const meta = {};
      for (const shareCode of downloadedTopics) {
        if (quizCache[shareCode]) {
          meta[shareCode] = quizCache[shareCode];
          continue;
        }
        try {
          const linkDoc = await getDoc(doc(db, "shareCodeLinks", shareCode));
          if (!linkDoc.exists()) continue;
          const linkData = linkDoc.data();
          // Use prefix of shareCode to determine collection
          let collectionType = shareCode.startsWith("u") ? "userQuizzes" : "officialQuizzes";
          const quizDoc = await getDoc(doc(db, collectionType, linkData.linksTo));
          if (quizDoc.exists()) {
            const title = quizDoc.data().title || shareCode;
            meta[shareCode] = { quizId: linkData.linksTo, type: collectionType, title };
            setQuizCache((prevCache) => ({ ...prevCache, [shareCode]: meta[shareCode] }));
          }
        } catch (err) {
          console.error("Error fetching quizMeta for", shareCode, err);
        }
      }
      setQuizMeta(meta);
      console.log("Quiz meta:", meta);
    };
    if (downloadedTopics.length > 0) fetchQuizMeta();
  }, [auth, db, downloadedTopics]); // Removed quizCache from dependencies

  // Utility to shuffle an array in-place
  function shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  // Fetch questions for selected topics/quizzes
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!auth.currentUser || selectedTopics.length === 0) {
        setQuestions([]);
        return;
      }
      try {
        const allQuestions = [];
        for (const shareCode of selectedTopics) {
          const meta = quizMeta[shareCode];
          if (!meta) continue;

          const questionsRef = collection(db, meta.type, meta.quizId, "questions");

          // Use Firestore's onSnapshot to listen for updates and cache data
          onSnapshot(questionsRef, (snapshot) => {
            const quizQuestions = snapshot.docs.map((doc) => {
              const questionData = doc.data();
              return {
                ...questionData,
                options: [...questionData.options].sort(() => Math.random() - 0.5),
                shareCode,
                id: doc.id,
              };
            });
            setQuizCache((prevCache) => ({
              ...prevCache,
              [shareCode]: { ...quizCache[shareCode], questions: quizQuestions },
            }));
            allQuestions.push(...quizQuestions);
            setQuestions(allQuestions.sort(() => Math.random() - 0.5));
          });
        }
      } catch (error) {
        console.error("Failed to load questions:", error);
      }
    };
    fetchQuestions();
  }, [auth, db, selectedTopics, quizMeta]);

  // Toggle topic selection and update in Firestore
  const toggleTopic = async (shareCode) => {
    let updatedTopics;
    if (selectedTopics.includes(shareCode)) {
      updatedTopics = selectedTopics.filter((t) => t !== shareCode);
    } else {
      updatedTopics = [...selectedTopics, shareCode];
    }
    setSelectedTopics(updatedTopics);
    if (auth.currentUser) {
      try {
        const userUID = auth.currentUser.uid;
        await updateDoc(doc(db, "users", userUID), {
          selectedTopics: updatedTopics,
        });
        console.log("Updated selectedTopics:", updatedTopics);
      } catch (error) {
        console.error("Failed to save selected topics:", error.message);
      }
    }
  };

  // Helper to get local date string in YYYY-MM-DD
  const getLocalDateString = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().split("T")[0];
  };

  // Handle answer and update dailyProgress
  const handleAnswer = async (option) => {
    if (selectedAnswer) return;
    setSelectedAnswer(option);

    const currentQuestion = questions[currentQuestionIndex];
    const isCorrect = option === currentQuestion.correctAnswer;
    setFeedback(isCorrect ? "Correct." : `Incorrect. The correct answer is: ${currentQuestion.correctAnswer}`);

    // Update dailyProgress in Firestore
    if (auth.currentUser) {
      try {
        const userUID = auth.currentUser.uid;
        const today = getLocalDateString(); // Use local date
        // Use the correct quizId (from quizMeta if available, else fallback)
        const quizId = quizMeta[currentQuestion.shareCode]?.quizId || currentQuestion.shareCode;
        const dailyProgressRef = doc(db, "users", userUID, "dailyProgress", today);
        const dailyProgressDoc = await getDoc(dailyProgressRef);
        const currentData = dailyProgressDoc.exists() ? dailyProgressDoc.data() : {};
        const quizProgress = currentData[quizId] || { answeredQuestions: 0, correctAnswers: 0 };
        await setDoc(dailyProgressRef, {
          ...currentData,
          [quizId]: {
            answeredQuestions: quizProgress.answeredQuestions + 1,
            correctAnswers: quizProgress.correctAnswers + (isCorrect ? 1 : 0)
          }
        }, { merge: true });
        console.log("Updated dailyProgress for", quizId, today);
      } catch (error) {
        console.error("Failed to update dailyProgress:", error.message);
      }
    }
  };

  const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  // Use downloadedTopics as the list of available topics/quizzes
  const allTopics = downloadedTopics;

  // Sort topics so selected ones appear first
  const sortedTopics = [...allTopics].sort((a, b) => {
    const aSelected = selectedTopics.includes(a);
    const bSelected = selectedTopics.includes(b);
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  });

  if (questions.length === 0 && selectedTopics.length > 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "#1E83F6", // Updated background color to blue
          minHeight: 320,
        }}
      >
        <div
          style={{
            transform: "scale(0.6) translateY(-45px)",
            transformOrigin: "top center",
          }}
        >
          <div
            style={{
              width: "60vw",
              maxWidth: 320,
              height: "200px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "-30px",
                width: "100%",
                height: "260px",
              }}
            >
              <Lottie
                animationData={loadingAnimation2} // Use the new animation
                loop
                style={{
                  width: "100%",
                  height: "100%",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <div
      className="text-white h-full flex flex-col"
      style={{
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: 150, // Increased bottom padding for better scrollability
        minHeight: "100vh",
        boxSizing: "border-box",
        backgroundColor: "#1E83F6", // Updated background color
      }}
    >
      {/* Topic Selection Slider */}
      <div className="overflow-x-auto pb-2" style={{ marginLeft: 14, marginRight: 14 }}>
        <div className="flex gap-2 min-w-fit">
          {sortedTopics.length === 0 ? (
            <div className="text-gray-400 px-4 py-2">No quizzes available. Add or download a quiz first.</div>
          ) : (
            sortedTopics.map((shareCode) => {
              const isSelected = selectedTopics.includes(shareCode);
              return (
                <div
                  key={shareCode}
                  className={`px-3 py-1 rounded-lg shadow cursor-pointer transition-colors duration-200`}
                  style={{
                    fontSize: "0.9rem", // Smaller font size
                    whiteSpace: "nowrap",
                    backgroundColor: isSelected ? "#ffffff" : "rgba(255, 255, 255, 0.3)", // Adjusted background colors
                    color: "#161616",
                    border: "none", // No border for both states
                    fontWeight: 500, // Medium font weight for topics
                  }}
                  onClick={() => toggleTopic(shareCode)}
                >
                  {quizMeta[shareCode]?.title || shareCode}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Question Area */}
      <div className="flex-1 flex flex-col overflow-y-auto">
        {questions.length > 0 ? (
          <>
            {/* Question Card */}
            <div
              className="mb-4"
              style={{
                backgroundColor: "#ffffff", // White background for question card
                color: "#161616",
                borderRadius: 49, // Match More Drills Card corner radius
                transform: `rotate(${Math.random() * 2 - 1}deg)`, // Random tiny rotation
                margin: "20px auto", // Increased margin-top and centered
                width: "98%", // Adjusted width for mobile
                maxWidth: 400, // Ensure it doesn't exceed max width
                padding: 32, // Adjusted padding for better fit
                boxShadow: "0 0 20px rgba(0, 0, 0, 0.3)", // Bigger drop shadow without offset
              }}
            >
              <div className="font-bold text-lg mb-2">{currentQuestion.question}</div>
              <div className="flex flex-col gap-2">
                {currentQuestion.options.map((option, idx) => {
                  let borderColor = "transparent";
                  if (selectedAnswer === option) {
                    if (option === currentQuestion.correctAnswer) {
                      borderColor = "#3be07a"; // green
                    } else {
                      borderColor = "#e05a5a"; // red
                    }
                  }
                  return (
                    <button
                      key={idx}
                      onClick={() => handleAnswer(option)}
                      className={`w-full py-2 px-3 rounded-lg bg-[#f8f8f5] hover:bg-[#e5e5e0] text-[#161616] transition-colors duration-200`}
                      style={{
                        border: `2px solid ${borderColor}`,
                        textAlign: "left",
                        fontWeight: 500, // Medium font weight for answers
                      }}
                    >
                      {option}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Feedback Card */}
            {selectedAnswer && (
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)", // 20% transparency
                  color: "#161616",
                  borderRadius: 49, // Match More Drills Card corner radius
                  marginTop: 16,
                  margin: "0 auto", // Center the card
                  width: "98%", // Adjusted width for mobile
                  maxWidth: 400, // Ensure it doesn't exceed max width
                  padding: 32, // Adjusted padding for better fit
                  boxShadow: "none", // No drop shadow
                }}
              >
                <p
                  className={
                    feedback.startsWith("Correct")
                      ? "font-semibold"
                      : feedback.startsWith("Incorrect")
                      ? "font-semibold"
                      : ""
                  }
                  style={{ color: "#000000" }} // Black color for Correct/Incorrect text
                >
                  {feedback}
                </p>
                {currentQuestion.explanation && (
                  <p className="text-black mt-2">{currentQuestion.explanation}</p>
                )}
                <button
                  onClick={() => {
                    setSelectedAnswer(null);
                    setFeedback("");
                    setCurrentQuestionIndex((prevIndex) => {
                      if (questions.length === 0) return 0;
                      if (prevIndex + 1 >= questions.length) {
                        setQuestions((qs) => shuffleArray([...qs]));
                        return 0;
                      }
                      return prevIndex + 1;
                    });
                  }}
                  className="mt-4 py-2 px-4 bg-white text-[#1E83F6] rounded-lg transition-colors duration-300 font-semibold"
                  style={{ float: "right" }} // Align button to the right
                >
                  Next Question
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-white">
            {allTopics.length === 0
              ? "No quizzes available. Add or download a quiz first."
              : "No questions available. Please select a topic."}
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizSection;
